import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ClockIconComponent, HoldIconComponent } from '@kuiper/icons';
import { finalize } from 'rxjs';
import { COLUMN_IDENTIFIERS } from '../../const';
import { FilterTableColByFeatFlagPipe } from '../../../../common/pipes/filter-table-col-by-featFlag/filter-table-col-by-feat-flag.pipe';
import { isFeatureFlaggedColumnVisible } from '../../../../common/helpers/feature-flagged-table-columns/feature-flagged-table-columns.helper';
import { RelatedRequestsDropdownComponent } from '../../../../../ui-components/lib/related-requests-dropdown/related-requests-dropdown.component';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { ToasterService } from '../../../../../ui-components/services/toast/toast.service';
import { ExportService } from '../../../../../ui-components/services/export/export.service';
import { TruncatePipe } from '../../../../../ui-components/pipes/truncate/truncate.pipe';
import { ModalServiceService } from '../../../../../ui-components/lib/modal/modal-service.service';
import { TrimTooltipDirective } from '../../../../../ui-components/directives/trim-tooltip/trim-tooltip.directive';
import { UserDropdownViewComponent } from '../../../../../ui-components/lib/user-dropdown-view/user-dropdown-view.component';
import { FilterOption } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.model';
import { FilterMapping } from '../../../../../ui-components/lib/filter-dropdown/filter-mapping.enum';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../ui-components/lib/table/table.model';
import { SlaDirective } from '../../../../common/directives/sla/sla.directive';
import { ActionListComponent } from '../../../../common/components/action-list/action-list.component';
import { RequestCreatorComponent } from '../../../../common/components/request-creator/request-creator.component';
import { TenantPathPipe } from '../../../../common/pipes/tenant-path/tenant-path.pipe';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { StepStatusPipe } from '../../../../common/pipes/step-status/step-status.pipe';
import { StepStatusEnum } from '../../../../common/enums/step-status';
import { RequestTypeEnum } from '../../../../common/enums/request-type';
import { Insight } from '../../../../common/models/insight.model';
import { ActionList } from '../../../../common/models/action-list.model';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../common/constants/pagination.constants';
import { EMPLOYEE_ROUTES } from '../../../../common/constants/employee.routing.constants';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { StepTask } from '../../../../common/models/assign-to-me.model';
import { CurrentAssignee } from '../../../../common/models/assignee.model';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { MyInboxService } from '../../../../common/services/my-inbox/my-inbox.service';
import {
  ApiResponse,
  FilteredTableComponent,
  FilteredTablePayload,
  Meta,
} from '../../../../common';
import {
  DELEGATED_TO_ME_COLUMNS,
  DELEGATED_TO_ME_FILTER_OPTIONS,
  DELEGATED_TO_ME_INSIGHTS,
  DELEGATED_TO_ME_INSIGHT_KEYS,
  DELEGATED_TO_ME_TABLE_FILTER_TABS,
  DELEGATED_TO_ME_VIEW_DETAILS_ACTION,
} from './delegated-to-me.constants';

@Component({
  selector: 'lib-delegated-to-me',
  imports: [
    DatePipe,
    SlaDirective,
    TranslocoPipe,
    AvatarComponent,
    TruncatePipe,
    TrimTooltipDirective,
    ActionListComponent,
    ReactiveFormsModule,
    ClockIconComponent,
    UserDropdownViewComponent,
    RequestCreatorComponent,
    RouterLink,
    TenantPathPipe,
    HoldIconComponent,
    BadgeComponent,
    StepStatusPipe,
    FilterTableColByFeatFlagPipe,
    FilteredTableComponent,
    RelatedRequestsDropdownComponent,
  ],
  templateUrl: './delegated-to-me.component.html',
  styleUrl: './delegated-to-me.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DelegatedToMeComponent implements OnInit {
  protected readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  protected readonly COLUMN_IDENTIFIERS = COLUMN_IDENTIFIERS;
  protected readonly stepStatusEnum = StepStatusEnum;
  protected readonly showProjectLink = true;

  private readonly myInboxService = inject(MyInboxService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly modalService = inject(ModalServiceService);
  private readonly toasterService = inject(ToasterService);
  private readonly exportService = inject(ExportService);
  private readonly tenantService = inject(TenantService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly showParentTasksColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.INBOX.parent_wf_column
    )
  );

  protected readonly showRelatedRequestsColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.parent_and_child_approach
    )
  );

  /******** pagination ********/
  meta = signal<Meta | undefined>(undefined);

  /*********** table  *********/
  isLoading = signal<boolean>(false);
  dataSource = signal<StepTask[]>([]);
  totalItems = signal<number>(0);
  readonly columns = computed<TableColumns[]>(() =>
    DELEGATED_TO_ME_COLUMNS.filter(col =>
      isFeatureFlaggedColumnVisible(col.header, {
        showParentTasksColumn: this.showParentTasksColumn(),
        showRelatedRequestsColumn: this.showRelatedRequestsColumn(),
      })
    ).map(column => ({
      ...column,
      header: this.transloco.translate(column.header),
      viewIconRouterLink: column.showViewIcon
        ? (row: StepTask) => this.getTaskRouterLink(row.id, row.flow.id)
        : undefined,
    }))
  );

  tableFilterTabs = signal<TableFilterTab[]>(
    DELEGATED_TO_ME_TABLE_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );

  readonly showInsights = input(true);
  readonly insightsChange = output<Insight[]>();
  readonly headerStartTemplate = input<TemplateRef<unknown> | null>(null);

  insights = signal<Insight[]>(DELEGATED_TO_ME_INSIGHTS);
  readonly displayedInsights = computed(() =>
    this.showInsights() ? this.insights() : []
  );
  payload = signal<FilteredTablePayload>({
    page: 1,
    size: 25,
    sortColumn: {
      key: '',
      value: 'asc',
    },
    filters: {},
    search: '',
    tab: this.tableFilterTabs()[0],
  });

  viewDetailsAction: ActionList = {
    ...DELEGATED_TO_ME_VIEW_DETAILS_ACTION,
    label: this.transloco.translate(DELEGATED_TO_ME_VIEW_DETAILS_ACTION.label),
  };

  filterOptions: FilterOption[] = DELEGATED_TO_ME_FILTER_OPTIONS.map(
    option => ({
      ...option,
      label: this.transloco.translate(option.label),
      placeholder: option.placeholder
        ? this.transloco.translate(option.placeholder)
        : '',
    })
  );

  filterMapping: Record<string, string> = {
    workflowTypes: FilterMapping.WORKFLOW_TYPES,
    creationDateStart: FilterMapping.CREATED_AFTER,
    creationDateEnd: FilterMapping.CREATED_BEFORE,
    projectIds: FilterMapping.HAS_FLOW_PROJECT_IDS,
  };

  ngOnInit(): void {
    if (this.showInsights()) {
      this.getInsights();
    }
  }

  getDelegatedRequests$ = () => {
    this.isLoading.set(true);
    this.meta.set(undefined);
    const { page, size, tab, sortColumn, filters, search } = this.payload();
    return this.myInboxService
      .getStepsDelegatedToMe(
        page,
        size,
        tab?.key ?? 'all',
        sortColumn,
        search,
        filters
      )
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      );
  };

  getDelegatedRequests() {
    this.getDelegatedRequests$().subscribe({
      next: (res: ApiResponse<StepTask>) => {
        this.handleGetRequestsSuccess(res);
      },
    });
  }

  handleGetRequestsSuccess(res: ApiResponse<StepTask>) {
    const data = res.data.map((item: StepTask) => ({
      ...item,
      current_assignees: item.assignees.map(
        assignee => assignee.data
      ) as unknown as CurrentAssignee[],
      assigneeType: item.assignees.length
        ? item.assignees.map(assignee => assignee.type)
        : [],
    }));
    this.dataSource.set(data);
    this.meta.set(res.meta);
  }

  getInsights() {
    this.myInboxService
      .getDelegatedToMeInsights()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: { data: Insight[] }) => {
        DELEGATED_TO_ME_INSIGHT_KEYS.forEach((key, index) => {
          this.insights()[index].value =
            res.data
              .find((insight: Insight) => insight.key === key)
              ?.value?.toString() ?? '0';
        });
        this.insightsChange.emit(
          this.insights().map(insight => ({ ...insight }))
        );
      });
  }

  getRelatedRequestViewLink = (childFlow: { id: number }) =>
    this.tenantService.tenantPath(
      `${EMPLOYEE_ROUTES.MY_INBOX.REQUEST}/${RequestTypeEnum.VIEW}/${childFlow.id}`
    );

  getTaskRouterLink(taskId: number, flowId: number): string[] {
    return [
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.MY_INBOX.LIST}/${EMPLOYEE_ROUTES.MY_INBOX.DELEGATED}/${taskId.toString()}/${RequestTypeEnum.ACTION}/${flowId.toString()}`
      ),
    ];
  }

  viewTaskDetails(task: StepTask) {
    this.router.navigate(this.getTaskRouterLink(task.id, task.flow.id));
  }

  getViewActionList(taskId: number, flowId: number): ActionList[] {
    this.viewDetailsAction.routerLinks = this.getTaskRouterLink(taskId, flowId);
    return [this.viewDetailsAction];
  }

  actionListItemClick(action: string, row: StepTask) {
    if (action === 'view') {
      this.router.navigate(this.getTaskRouterLink(row.id, row.flow.id));
    }
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  submitExportForm(form: FormGroup) {
    const formValue = form.value;
    this.myInboxService
      .exportDelegatedToMe(formValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: unknown) => {
          this.exportService.handleExportSuccess(
            response as Response,
            form,
            () => this.closeModal()
          );
        },
        error: (error: { error?: { message?: string } }) => {
          this.toasterService.add(
            error.error?.message ?? '',
            '',
            '',
            'error',
            true,
            true
          );
          this.exportService.reInitExportForm(form);
          this.closeModal();
        },
      });
  }
}
